import { DEFAULT_CYCLE_STAGES } from './constants';

const MS_IN_DAY = 24 * 60 * 60 * 1000;

const toStartOfDay = (date) => {
  const d = new Date(date.getTime());
  d.setHours(0, 0, 0, 0);
  return d;
};

const parseDate = (dateInput) => {
  if (!dateInput) return null;
  if (dateInput instanceof Date && !Number.isNaN(dateInput.getTime())) {
    return toStartOfDay(dateInput);
  }

  const parsed = new Date(dateInput);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return toStartOfDay(parsed);
};

export const getTotalCycleLength = (stages = []) =>
  stages.reduce((total, stage) => {
    const length = Number(stage?.length);
    return total + (Number.isFinite(length) && length > 0 ? Math.round(length) : 0);
  }, 0);

export const normalizeStages = (stages, fallbackStages = DEFAULT_CYCLE_STAGES) => {
  if (!Array.isArray(stages) || stages.length === 0) {
    return fallbackStages.map((stage) => ({ ...stage }));
  }

  return stages.map((stage, index) => {
    const fallback = fallbackStages[index % fallbackStages.length];
    const lengthNumber = Number(stage?.length);
    return {
      id: stage?.id || fallback.id || `stage-${index}`,
      name: stage?.name || fallback.name,
      length: Number.isFinite(lengthNumber) && lengthNumber > 0 ? Math.round(lengthNumber) : fallback.length,
    };
  });
};

export const calculateCycleDay = (cycleStartDate, stages, referenceDate = new Date()) => {
  const startDate = parseDate(cycleStartDate);
  if (!startDate) return null;

  const refDate = parseDate(referenceDate);
  if (!refDate) return null;

  const totalLength = getTotalCycleLength(stages);
  if (totalLength <= 0) return null;

  const diffDays = Math.floor((refDate.getTime() - startDate.getTime()) / MS_IN_DAY);

  // If reference date is before start date, calculate backwards through previous cycles
  if (diffDays < 0) {
    const cyclesBack = Math.ceil(Math.abs(diffDays) / totalLength);
    const daysIntoPreviousCycle = (cyclesBack * totalLength + diffDays) % totalLength;
    // Convert 0-indexed to 1-indexed: day 0 = cycle day 1, day 1 = cycle day 2, etc.
    return daysIntoPreviousCycle + 1;
  }

  // Convert 0-indexed to 1-indexed: day 0 = cycle day 1, day 1 = cycle day 2, etc.
  const dayIndex = diffDays % totalLength;
  return dayIndex + 1; // 1-indexed day within cycle
};

export const determineCycleStage = (cycleStartDate, stages, referenceDate = new Date()) => {
  const normalizedStages = normalizeStages(stages);
  const cycleDay = calculateCycleDay(cycleStartDate, normalizedStages, referenceDate);

  if (!cycleDay) {
    return { stage: null, dayOfStage: null, cycleDay: null, totalLength: getTotalCycleLength(normalizedStages) };
  }

  let remaining = cycleDay;
  for (const stage of normalizedStages) {
    if (remaining <= stage.length) {
      return {
        stage,
        dayOfStage: remaining,
        cycleDay,
        totalLength: getTotalCycleLength(normalizedStages),
      };
    }
    remaining -= stage.length;
  }

  // Should not reach here, but fall back to last stage
  const lastStage = normalizedStages[normalizedStages.length - 1];
  return {
    stage: lastStage,
    dayOfStage: lastStage?.length || null,
    cycleDay,
    totalLength: getTotalCycleLength(normalizedStages),
  };
};

export const validateCycleStages = (stages, { requireMinStages = true, strict = false } = {}) => {
  const errors = [];
  const stagesToCheck = (() => {
    if (strict) {
      if (!Array.isArray(stages)) return [];
      return stages.map((stage, index) => ({
        id: stage?.id || `stage-${index}`,
        name: stage?.name ?? '',
        length: stage?.length,
      }));
    }
    return normalizeStages(stages);
  })();

  if (requireMinStages && stagesToCheck.length < 1) {
    errors.push('At least one stage is required.');
  }

  let totalLength = 0;

  stagesToCheck.forEach((stage, index) => {
    if (!stage.name || !stage.name.trim()) {
      errors.push(`Stage ${index + 1} needs a name.`);
    }
    const lengthNumber = Number(stage.length);
    if (!Number.isFinite(lengthNumber) || lengthNumber <= 0) {
      errors.push(`Stage ${index + 1} should have a positive length.`);
    } else {
      totalLength += Math.round(lengthNumber);
    }
  });

  if (totalLength <= 0) {
    errors.push('Combined stage length must be greater than zero.');
  }

  return {
    isValid: errors.length === 0,
    errors,
    stages: stagesToCheck,
    totalLength,
  };
};

export const getCycleSummary = (cycleTracking, referenceDate = new Date()) => {
  if (!cycleTracking || !cycleTracking.enabled) {
    return {
      enabled: false,
      cycleDay: null,
      stage: null,
      totalLength: 0,
    };
  }

  const stages = normalizeStages(cycleTracking.stages);
  const { stage, dayOfStage, cycleDay, totalLength } = determineCycleStage(
    cycleTracking.cycleStartDate,
    stages,
    referenceDate,
  );

  return {
    enabled: true,
    cycleDay,
    stage,
    dayOfStage,
    totalLength,
  };
};


